import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../util/errors';
import { getFinanceStatus } from '../util/api';

/**
 * Financing status for the signed-in user.
 *
 * Skip is not the source of truth here — the finance team records decisions
 * in HubSpot, and `/api/finance-status` reads that back server-side. Nothing
 * in this file derives, infers or defaults a status.
 *
 * `available` is kept distinct from `status` on purpose. A null status with
 * available:true means "we checked and there is no application on file"; a
 * null status with available:false means "we could not check". Collapsing
 * those two would tell a buyer with a live application that they have none.
 */

export const fetchFinanceStatusPayloadCreator = async (arg, thunkAPI) => {
  const { rejectWithValue } = thunkAPI;
  try {
    const data = await getFinanceStatus();
    if (!data?.success) {
      throw new Error('finance status lookup unsuccessful');
    }
    return { status: data.status ?? null, available: !!data.available };
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const fetchFinanceStatusThunk = createAsyncThunk(
  'app/financeStatus/fetch',
  fetchFinanceStatusPayloadCreator
);

export const fetchFinanceStatus = () => dispatch => dispatch(fetchFinanceStatusThunk());

const initialState = {
  status: null,
  available: false,
  fetchInProgress: false,
  fetchError: null,
  loaded: false,
};

const financeStatusSlice = createSlice({
  name: 'app/financeStatus',
  initialState,
  reducers: {
    clearFinanceStatus: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(fetchFinanceStatusThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(fetchFinanceStatusThunk.fulfilled, (state, action) => {
        state.fetchInProgress = false;
        state.status = action.payload.status;
        state.available = action.payload.available;
        state.loaded = true;
      })
      .addCase(fetchFinanceStatusThunk.rejected, (state, action) => {
        state.fetchInProgress = false;
        state.fetchError = action.payload;
        // Not loaded, not available, and explicitly no status: a failed
        // lookup must never leave anything approval-shaped behind.
        state.status = null;
        state.available = false;
      });
  },
});

export const { clearFinanceStatus } = financeStatusSlice.actions;
export default financeStatusSlice.reducer;
